import { AnalyticsSalesIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/analytics/AnalyticsSalesIcon";
import { CertificatesIssuedIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/analytics/CertificatesIssuedIcon";
import { ConcludedMissionsIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/analytics/ConcludedMissionsIcon";
import { EngagementIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/analytics/EngagementIcon";
import { ExportsIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/analytics/ExportsIcon";
import { ProgressIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/analytics/ProgressIcon";
import { QuizzAnswersIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/analytics/QuizzAnswersIcon";
import { UsersIcon } from "pages/[tenant]/console/assets/icons/sidebarSection/analytics/UsersIcon";
import {
  IOption,
  IOptionsHook,
  ISubSection,
} from "pages/[tenant]/console/types";

import { useAuthContext } from "shared/utils/hooks/useAuthContext";
import { useFeature } from "shared/utils/hooks/useFeature";
import { usePlanFeature } from "shared/utils/hooks/usePlanFeature";

import { BarChart } from "@Console/assets/icons/sidebarSection/analytics/BarChart";
import BlankChart from "@Console/assets/icons/sidebarSection/analytics/BlankChart";
import { BoxIcon } from "@Console/assets/icons/sidebarSection/analytics/BoxIcon";
import { DashPerformance } from "@Console/assets/icons/sidebarSection/analytics/DashPerformance";
import { LineChart } from "@Console/assets/icons/sidebarSection/analytics/LineChart";
import SubscriptionDashboard from "@Console/assets/icons/sidebarSection/sales/SubscriptionDashboard";
import {
  permissionsExport,
  permissionsImport,
} from "@Console/utils/permissionsAreasAdminConsole";

export const useAnalyticsOptions = (): IOptionsHook => {
  const { rolesUser, loggedUser } = useAuthContext();

  const isAdmin =
    loggedUser?.admin ||
    loggedUser?.superadmin ||
    loggedUser?.console_role?.slug === "superadmin" ||
    loggedUser?.console_role?.slug === "admin";

  const hasPermission = (permissionsList: string[]): boolean =>
    rolesUser?.some((permission) => permissionsList?.includes(permission));

  const isStatisticsEnabled = usePlanFeature("f_usage_statistics_dashboard");
  const isCsatEnabled = useFeature("f_csat_feedbacks");
  const isGamificationDashEnabled = useFeature("f_gamification_metrics");

  const isImportUsersEnabled = useFeature("f_import_users");
  const isImportAccessEnabled = useFeature("f_import_accesses");
  const isImportEnabled = isImportUsersEnabled || isImportAccessEnabled;
  const isConsoleDashboardSubscriptionsEnabled =
    useFeature("f_access_dashboard") &&
    hasPermission(["visualizar-dashboard-de-acessos"]);

  const sectionLabel = "Dados";
  const analytics: IOption[] = [
    {
      id: 1,
      label: "Progresso de cohorts",
      path: "/console/progress",
      icon: <ProgressIcon />,
      shouldHide: true,
    },
    {
      id: 2,
      label: "Usuários",
      path: "/console/dashboards/users",
      icon: <UsersIcon />,
      shouldHide: true,
    },
    {
      id: 3,
      label: "Engajamento",
      path: "/console/dashboards/engagement",
      icon: <EngagementIcon />,
      shouldHide: true,
    },
    {
      id: 4,
      label: "Vendas",
      path: "/console/dashboards/sales",
      icon: <AnalyticsSalesIcon />,
      shouldHide: true,
    },

    {
      id: 5,
      label: "Quizzes",
      path: "/console/quizzes",
      icon: <QuizzAnswersIcon />,
      shouldHide: true,
    },
    {
      id: 6,
      label: "Emissão de certificados",
      path: "/console/certificates",
      icon: <CertificatesIssuedIcon />,
      shouldHide: true,
    },
    {
      id: 7,
      label: "Missões",
      path: "/console/missions",
      icon: <ConcludedMissionsIcon />,
      shouldHide: true,
    },
    {
      id: 8,
      label: "Dataset export",
      description:
        "Exporte conjuntos inteiros de dados para elaboração de relatórios ou análise de dados em ferramentas externas.",
      path: "/console/reports",
      icon: <ExportsIcon />,
      shouldHide: !hasPermission(permissionsExport),
    },
    {
      id: 9,
      label: "Importação em lote",
      path: "/console/imports",
      icon: <BoxIcon />,
      shouldHide: !hasPermission(permissionsImport) || !isImportEnabled,
    },
  ];

  const dataSubSection: ISubSection[] = [
    {
      label: "Dashboard",
      options: [
        {
          id: 14,
          label: "Assinaturas",
          description:
            "Acompanhe o desempenho das suas assinaturas e tome decisões baseadas em dados.",
          path: "/console/subscription-dashboard",
          icon: <SubscriptionDashboard />,
          shouldHide: !isConsoleDashboardSubscriptionsEnabled,
        },
        {
          id: 10,
          label: "Aproveitamento",
          path: "/console/performance",
          icon: <DashPerformance />,
          shouldHide: !hasPermission([
            "visualizar-dashboard-de-aproveitamento",
          ]),
        },
        {
          id: 11,
          label: "Estatística de uso",
          path: "/console/user-activity",
          icon: <BarChart />,
          shouldHide:
            !isStatisticsEnabled ||
            !hasPermission(["visualizar-dashboard-de-estatisticas-de-uso"]),
        },
        {
          id: 12,
          label: "Satisfação do cliente",
          description:
            "Veja como está o sentimento dos seus alunos em relação ao seu conteúdo.",
          path: "/console/satisfaction-survey",
          icon: <BlankChart />,
          shouldHide: !isCsatEnabled || !isAdmin,
        },
        {
          id: 13,
          label: "Dados da gamificação",
          path: "/console/gamification-dash",
          icon: <LineChart />,
          shouldHide: !isGamificationDashEnabled,
        },
      ],
    },
  ];

  return {
    options: analytics,
    subSections: dataSubSection,
    label: sectionLabel,
  };
};
